import { useState, useEffect } from "react";
import { useApolloClient, OperationVariables, DocumentNode, ApolloQueryResult, FetchPolicy } from "@apollo/client";

interface QueryConfig<TData = any, TVariables = OperationVariables> {
	query: DocumentNode;
	variables?: TVariables;
	fetchPolicy?: FetchPolicy;
	context?: Record<string, any>;
}

interface BatchState<T extends any[]> {
	data: T | null;
	loading: boolean;
	error: any;
	refetch: () => void;
}

export function useBatchQuery<T extends any[]>(
	configs: { [K in keyof T]: QueryConfig<T[K]> },
	commonConfig?: { lazy?: boolean }
): BatchState<T> {
	const client = useApolloClient();
	const [fetchStartTime, setFetchStartTime] = useState(commonConfig?.lazy ? null : Date.now());
	const [state, setState] = useState<BatchState<T>>({
		data: null,
		loading: true,
		error: null,
		refetch: () => setFetchStartTime(Date.now()),
	});

	const queryKey = JSON.stringify(configs.map((c) => (c.query.definitions[0] as any)?.name?.value));

	useEffect(() => {
		if (!fetchStartTime) {
			return;
		}

		let isMounted = true;

		const fetchAll = async () => {
			try {
				const promises = configs.map((config) =>
					client.query({
						query: config.query,
						variables: config.variables,
						context: config.context,
						fetchPolicy: config.fetchPolicy,
					})
				);

				const results = await Promise.all(promises);

				if (isMounted) {
					const dataArray = results.map((r) => r.data) as unknown as T;

					setState((state) => ({
						...state,
						data: dataArray,
						loading: false,
						error: null,
					}));
				}
			} catch (err) {
				if (isMounted) {
					setState((state) => ({ ...state, data: null, loading: false, error: err }));
				}
			}
		};

		fetchAll();

		return () => {
			isMounted = false;
		};
	}, [queryKey, client, fetchStartTime]);

	return state;
}
